import { usePermission } from "hooks/usePermission";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { Icons } from "assets/icons";
import { formatUTCDate, getLocalizedField, getMinMaxDate } from "utils/utils";
import { getActionFlags } from "utils/getActionFlags";

import FilterBar from "components/common/FilterBar";
import Switch from "components/common/Switch";
import { showDeleteModal } from "store/slices/modalSlice";
import { UserStatusBadge } from "components/common/StatusBadge";
import TableShimmer from "components/common/TableShimmer";
import NoDataFound from "components/common/NoDataFound";
import { handleStatusUpdate } from "helpers/apiUtils";
import { ORDER_BY_OPTIONS, STATUS_OPTIONS } from "utils/constants";
import Paginations from "components/common/Paginations";
import DataTable from "components/common/table/DataTable";
import {
  getStoreCameras,
  resetFilters,
  setFilter,
  updateStoreCameraStatus,
} from "store/slices/storeCameraSlice";
import PageHeader from "components/common/PageHeader";

const StoreCamera = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { trash, edit, noUsers, noPlans, view } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("localserver");

  const {
    items: storeCameras,
    loading,
    isLoading,
    isDeleting,
    loadingItemId,
    totalPages,
    currentPage,
    totalCount,
    filters: { keyword, dates, status, orderBy },
  } = useSelector((state) => state.StoreCamera);

  useEffect(() => {
    if (canView) {
      getAllData();
    }
  }, [canView, isDeleting]);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage: 50,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      status: isReset ? -1 : status,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getStoreCameras(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = [
    {
      key: "status",
      placeholder: t("Status"),
      type: "singleSelect",
      options: STATUS_OPTIONS,
    },
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
  ];

  const columns = useMemo(
    () => [
      {
        header: t("Store"),
        key: "branchId",
        render: (value) => getLocalizedField(value?.name, language) || "-",
      },
      {
        header: t("Name"),
        key: "name",
        render: (value) => value || "-",
      },

      {
        header: t("Camera Id"),
        key: "cameraId",
        render: (value) => value || "-",
      },
      {
        header: t("Role"),
        key: "role",
        render: (value) => value || "-",
      },
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <UserStatusBadge status={value} t={t} />,
      },
    ],
    [t],
  );

  const renderActions = useCallback(
    (item) => (
      <>
        {canEdit && (
          <>
            {item.status !== -1 && (
              <Switch
                checked={item.status === 1}
                handleOnChange={() => {
                  handleStatusUpdate({
                    _id: item._id,
                    status: item.status,
                    dispatch,
                    action: updateStoreCameraStatus,
                  });
                }}
                disabled={isLoading && loadingItemId === item._id}
              />
            )}
            <img
              src={edit}
              width={20}
              height={20}
              alt="edit"
              onClick={() => navigate(`/store-cameras/edit?id=${item._id}`)}
            />
          </>
        )}
        {canView && (
          <Link to={`/store-cameras/details?id=${item._id}`}>
            <img src={view} width={20} height={20} alt="view" />
          </Link>
        )}
        {canDelete && (
          <img
            src={trash}
            width={20}
            height={20}
            alt="delete"
            onClick={() => {
              dispatch(
                showDeleteModal({
                  title: t("Delete camera"),
                  message: t("Are you sure you want to delete this camera?"),
                  itemId: item._id,
                  actionType: "STORE_CAMERA",
                }),
              );
            }}
          />
        )}
      </>
    ),
    [canEdit, canDelete, isLoading, loadingItemId],
  );

  if (!canView) return null;

  return (
    <div className="page-content users">
      <PageHeader isBackBtn={true} title={t("Store Camera")} />
      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={"Search by Name , Camera Id"}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={canAdd}
        btnText={t("Add Camera")}
        onBtnClick={() => navigate("/store-cameras/add")}
      />

      {loading && <TableShimmer rows={12} columns={8} />}

      {!loading && storeCameras?.length === 0 && (
        <NoDataFound title={t("No cameras found")} icon={noPlans} />
      )}

      {!loading && storeCameras?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={storeCameras}
            renderActions={renderActions}
            t={t}
          />
          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default StoreCamera;
